import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { saveAs } from 'file-saver';
import {
  badgeLabel,
  photoProofBadge,
  photoProofNote,
  photoProofOf,
  photoProofRows,
  PhotoProofRow,
  DEFAULT_VERIFIED_ACCURACY_M,
} from '../../helpers/photo-proof';

/**
 * Photo proof details (GeoStamp, mobile 7.3): the badge, the rows a reviewer
 * reads (as on the phone's Proof sheet) and the stamped copy to download.
 * Shared by the carousel dialog and the map's picture overlay.
 */
@Component({
  selector: 'app-photo-proof-panel',
  templateUrl: './photo-proof-panel.component.html',
  styleUrls: ['./photo-proof-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PhotoProofPanelComponent {
  @Input() picture: any;
  @Input() thresholdM = DEFAULT_VERIFIED_ACCURACY_M;
  /** True while the viewer shows the untouched original instead of the stamped copy. */
  @Input() showingOriginal = false;
  @Output() toggleOriginal = new EventEmitter<void>();

  get proof(): any | null {
    return photoProofOf(this.picture);
  }

  get badge(): string | null {
    return photoProofBadge(this.picture, this.thresholdM);
  }

  get badgeLabel(): string {
    return badgeLabel(photoProofBadge(this.picture, this.thresholdM) as any, this.proof);
  }

  get rows(): PhotoProofRow[] {
    return photoProofRows(this.picture);
  }

  get sourceNote(): string {
    return photoProofNote(this.proof);
  }

  get mapsLink(): string | null {
    const lat = Number(this.proof?.latitude);
    const lng = Number(this.proof?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? `https://www.google.com/maps?q=${lat},${lng}` : null;
  }

  /** The server-burned copy with the stamp, when the phone asked for one. */
  downloadStamped() {
    if (!this.picture?.stamped_url) {
      return;
    }
    const base = (this.picture.original_name || 'image').replace(/\.[^.]+$/, '');
    saveAs(this.picture.stamped_url, `${base}-stamped.jpeg`);
  }
}
